/**
 * Turning the activity feed into the series the charts draw.
 *
 * `history.list` returns every purchase, sale and misc expense with a date, an
 * amount and a counterparty. That is enough to build the whole dashboard —
 * yearly totals, a monthly breakdown, and the individual transactions behind any
 * bar — from a single request, rather than a call per drill-down step.
 *
 * These are plain functions rather than reducers or memoised selectors on
 * purpose: they take data and arguments and return a result, so each one can be
 * tested directly, and the components memoise the calls themselves.
 *
 * A note on dates. Every `at` is an ISO string, and the year and month are read
 * out of the *string* rather than by constructing a Date. A purchase dated
 * 2026-03-01 is stored as a DATE and serialised as midnight UTC; parsing it into
 * a local Date can move it across a month boundary in some time zones, which
 * would silently file a transaction under the wrong month. Slicing the string
 * gives the date the database actually holds, which is what the server's own
 * YEAR()/MONTH() reporting uses.
 */

/** The three measures, in the fixed order their colours are assigned in. */
export const MEASURES = ['sales', 'purchases', 'misc'];

/** Which feed entry type feeds which measure. */
const ENTRY_TYPE_FOR_MEASURE = {
  sales: 'sale',
  purchases: 'purchase',
  misc: 'misc'
};

const yearOf = (entry) => Number(entry.at.slice(0, 4));
const monthOf = (entry) => Number(entry.at.slice(5, 7)) - 1;

/** Localised short month names, so the axis matches the reader's own locale. */
export function monthLabels(locale = 'en-IN') {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'short' });
  // 2020 is an arbitrary non-leap-sensitive year; only the month name is used.
  return Array.from({ length: 12 }, (_, month) => formatter.format(new Date(2020, month, 1)));
}

/** Every year the feed has activity in, newest first. */
export function listYears(entries = []) {
  const years = new Set(entries.map(yearOf));
  return [...years].sort((a, b) => b - a);
}

/**
 * Twelve rows — one per month of the given year — with a total per measure.
 *
 * Months with no activity are present and zero rather than missing, so the axis
 * always shows a full year and a gap reads as "nothing happened" instead of
 * being silently closed up.
 */
export function monthlyTotals(entries = [], year) {
  const rows = Array.from({ length: 12 }, (_, month) => ({ month, sales: 0, purchases: 0, misc: 0 }));

  for (const entry of entries) {
    if (yearOf(entry) !== year) continue;

    const measure = MEASURES.find((name) => ENTRY_TYPE_FOR_MEASURE[name] === entry.type);
    if (!measure) continue;

    rows[monthOf(entry)][measure] += Number(entry.total) || 0;
  }

  return rows;
}

/** The entries behind one measure in one month. */
export function entriesFor(entries = [], { year, month, measure }) {
  const type = ENTRY_TYPE_FOR_MEASURE[measure];

  return entries.filter(
    (entry) =>
      entry.type === type &&
      yearOf(entry) === year &&
      (month === null || month === undefined || monthOf(entry) === month)
  );
}

/** The label used to group a transaction in the second-level chart. */
function groupOf(entry) {
  // Misc spend groups by what it was for; a lot-linked expense already carries
  // its supplier as the counterparty, which would bury the description.
  if (entry.type === 'misc') return entry.description || entry.counterparty || '—';
  return entry.counterparty || '—';
}

/**
 * One month's spend or revenue, grouped by counterparty and ranked.
 *
 * Everything past `limit` is folded into a single "Other" row rather than given
 * a colour of its own — a long tail of near-identical hues is unreadable, and
 * the fold keeps the total honest. "Other" stays last regardless of its size, so
 * it is never mistaken for a real counterparty that happens to rank highly.
 */
export function breakdownByCounterparty(entries = [], { year, month, measure, limit = 8, otherLabel = 'Other' }) {
  const totals = new Map();

  for (const entry of entriesFor(entries, { year, month, measure })) {
    const name = groupOf(entry);
    const current = totals.get(name) ?? { name, total: 0, count: 0 };
    current.total += Number(entry.total) || 0;
    current.count += 1;
    totals.set(name, current);
  }

  const ranked = [...totals.values()].sort((a, b) => b.total - a.total);
  if (ranked.length <= limit) return ranked;

  const head = ranked.slice(0, limit);
  const tail = ranked.slice(limit);

  return [
    ...head,
    {
      name: otherLabel,
      total: tail.reduce((sum, row) => sum + row.total, 0),
      count: tail.reduce((sum, row) => sum + row.count, 0),
      isOther: true,
      // Kept so drilling into "Other" can still list what it stands for.
      members: tail.map((row) => row.name)
    }
  ];
}

/**
 * The individual transactions behind one bar of the second-level chart.
 *
 * `group` may be the "Other" bucket, in which case its members are matched
 * rather than its label — nothing is actually filed under the name "Other".
 */
export function transactionsFor(entries = [], { year, month, measure, group, members }) {
  const matches = members ? new Set(members) : null;

  return entriesFor(entries, { year, month, measure })
    .filter((entry) => (matches ? matches.has(groupOf(entry)) : groupOf(entry) === group))
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

/** Totals for a whole year, used for the chart's summary line. */
export function yearTotals(entries = [], year) {
  return monthlyTotals(entries, year).reduce(
    (sum, row) => ({
      sales: sum.sales + row.sales,
      purchases: sum.purchases + row.purchases,
      misc: sum.misc + row.misc
    }),
    { sales: 0, purchases: 0, misc: 0 }
  );
}
